import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {IE3CL707} from '@portbase/bezoekschip-service-typescriptmodels';

/**
 * Thin, shared label+card wrapper for a single searched/selected party field
 * (Release-to party / Container operator / Inland operator) on the commercial-release modal's
 * Parties tab. The label sits OUTSIDE any card (`d-block fw-regular mb-3`), and the resolved
 * entity is shown in a `card > card-body.p-4`, matching consignments' visual language
 * (`consignment-carrier-details.component.html`).
 *
 * Founder-reported regression fix (traced this session, not guessed): this component used to
 * conflate two independent things into one `innerEditMode` flag - (a) whether the projected
 * `<app-search>` (the re-search/replace-the-whole-entity control) is visible at all, and (b)
 * whether the resolved entity's own sub-fields were being edited in place. That made the search
 * bar disappear the instant the user saved once, requiring an extra pencil click to bring it
 * back - very different from how consignments actually behaves.
 *
 * Consignments' real precedent (`consignment-carrier-details.component.html` lines 1-16,
 * `consignment-party-details.component.html` lines 31-49) keeps these as TWO INDEPENDENT sibling
 * blocks: `@if (editMode) { <app-search> }` stays visible for the ENTIRE time the outer
 * `editMode` is true, never gated by any inner per-field edit toggle; the display card
 * (`@if (entity) { <card>... }`) is a separate block below it that shows whenever something is
 * selected, regardless of `editMode`. Only Carrier/Consignee/Consignor's OWN inner
 * `carrierEditMode`/`partyEditMode` toggle (pencil + Cancel/Save) additionally gates whether the
 * CARD's sub-fields are individually editable - because those parties have genuinely
 * editable sub-fields (name, street, city, ...).
 *
 * None of the three commercial-release parties (`ReleaseToParty`, `Carrier`-as-container-operator,
 * `InlandOperator`) support per-field in-place editing here - all three are always REPLACED as a
 * whole via search, never edited field-by-field (the `richAddress`/`basicAddress` grids below are
 * read-only displays only). So the inner-edit-mode/pencil/Cancel/Save skeleton that used to live
 * here was vestigial - copied from `ConsignmentSupplementaryDeclarantComponent`'s inner-toggle
 * precedent (which DOES edit a string sub-field in place) but never actually applicable. It has
 * been removed: the projected `<app-search>` is now ALWAYS visible whenever the outer `editMode`
 * is true (no extra click required), and the display card is always shown whenever `entity` is
 * truthy - independent of `editMode` - exactly matching consignments' outer search/card structure.
 *
 * There is no more `entityChange` output either: the projected `<app-search>` is authored by the
 * grandparent (`commercial-release-modal-parties.component.html`) with its OWN
 * `[(ngModel)]="data.<party>"` bound directly onto the parent-scope field, so selecting a new
 * entity already updates that field directly - this wrapper's `[entity]` input just re-reads the
 * same reference on the next change-detection pass to render the display card. There was never a
 * genuine need for this wrapper to relay a change upward itself.
 *
 * `richAddress` (Container-operator-only): Container operator is bound to the generated `Carrier`
 * type, which - unlike `ReleaseToParty`/`InlandOperator` - carries a full postal address
 * (`Carrier.address`) plus an EORI number (`Carrier.customsId`), exactly like consignments' own
 * Carrier field. The grid mirrors `consignment-carrier-details.component.html`'s
 * Street/Number/PO box/Postal code/City/Region/Country/Telephone/Email layout, read-only, using
 * safe navigation since a `Carrier` from `FindCarriers`/`GetMyCarriers` is not guaranteed to have
 * `address`/`communications` populated.
 *
 * `basicAddress` (Release-to-party-only): Release-to party is mapped from `FindCustomers`'
 * `ApiCustomer.customerData` (an untyped `any`), which - per the real mock fixture
 * (`bezoekschip-query-handler.mock.ts`) and the unrelated `ReleaseToPartyOverviewComponent`'s own
 * Excel export that reads the exact same query - genuinely carries `address`/`zipCode`/`city` at
 * runtime. That is a smaller field set than Container operator's `Carrier.address` (no
 * number/PO box/region/country/telephone/email - those simply are not present on `customerData`),
 * so it gets its OWN smaller grid rather than being forced into the 11-field Carrier layout.
 *
 * `eanGrid` (Inland-operator-only): founder request - "Can you look at the fields we CAN show for
 * Inland operator? EAN code is really important here." The generated `InlandOperator` model
 * (`typescript-models/common.d.ts`) has ONLY `name`/`ean`/`portbaseId` - no address at all, a real
 * domain-model limitation, not a wiring gap, so no richer variant applies here the way it does for
 * the other two parties. `portbaseId` is deliberately left off this grid: it is an internal id,
 * never rendered as a user-facing labeled field anywhere else in this codebase (checked - not even
 * on Release-to party's own `basicAddress` grid above, which has the same field on its underlying
 * `ApiCustomer` and also omits it). The grid shows exactly Name + EAN code, matching the founder's
 * own phrasing, replacing the single collapsed `name (ean)` line that used to bury the EAN inside
 * one formatted string.
 */
@Component({
  selector: 'app-commercial-release-party-field',
  templateUrl: './commercial-release-party-field.component.html',
  styleUrls: ['./commercial-release-party-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CommercialReleasePartyFieldComponent {
  @Input() label: string;
  @Input() formatter: (value: any) => string;
  @Input() entity: any;
  @Input() editMode: boolean;
  @Input() required: boolean = false;
  // See class doc: Container-operator-only richer read-only field grid (Carrier's address + EORI).
  @Input() richAddress: boolean = false;
  // See class doc: Release-to-party-only smaller read-only address grid (customerData's address).
  @Input() basicAddress: boolean = false;
  // See class doc: Inland-operator-only read-only Name/EAN code grid (InlandOperator's own fields).
  @Input() eanGrid: boolean = false;

  // Read-only lookups into `Carrier.communications` for the `richAddress` grid - mirrors
  // `ConsignmentCarrierDetailsComponent.telephone`/`.email`, minus the `defaultTelephone`/
  // `defaultEmail` edit-mode fallback objects, since this grid never edits a communication in place.
  get richAddressTelephone(): string {
    return this.entity?.communications?.find(c => c.type === IE3CL707.TE)?.identifier;
  }

  get richAddressEmail(): string {
    return this.entity?.communications?.find(c => c.type === IE3CL707.EM)?.identifier;
  }
}
